import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchSkills, fetchSkillTemplates } from '@/api/skills/api';
import { type AnalysisSkillOption } from '@/hooks/useTranscriptAnalysis';
import {
  readAnalysisSkillSelection,
  writeAnalysisSkillSelection,
} from '@/utils/assessmentFlowRoute';
import {
  BUNDLED_TRANSCRIPT_ANALYSIS_TEMPLATE_KEY,
  skillTagsIncludeTranscriptAnalysis,
} from '@/utils/transcriptAnalysisSkillFilter';

export interface UseAnalysisSkillOptionsOptions {
  assessmentInstanceId: string;
  accessToken: string | null;
}

export interface UseAnalysisSkillOptionsResult {
  analysisSkillOptions: AnalysisSkillOption[];
  selectedAnalysisSkillValue: string | null;
  skillsLoading: boolean;
  bundledOption: AnalysisSkillOption;
  setSelectedAnalysisSkillValue: (v: string | null) => void;
}

/**
 * Load transcript-analysis skill options and manage persisted skill selection.
 */
export function useAnalysisSkillOptions({
  assessmentInstanceId,
  accessToken,
}: UseAnalysisSkillOptionsOptions): UseAnalysisSkillOptionsResult {
  const [analysisSkillOptions, setAnalysisSkillOptions] = useState<AnalysisSkillOption[]>([]);
  const [selectedAnalysisSkillValue, setSelectedAnalysisSkillValueState] = useState<string | null>(
    null,
  );
  const [skillsLoading, setSkillsLoading] = useState(false);

  const bundledOption = useMemo<AnalysisSkillOption>(
    () => ({
      value: `template:${BUNDLED_TRANSCRIPT_ANALYSIS_TEMPLATE_KEY}`,
      label: 'Bundled: Transcript Analysis',
    }),
    [],
  );

  useEffect(() => {
    if (!accessToken) {
      setAnalysisSkillOptions([bundledOption]);
      const stored = readAnalysisSkillSelection(assessmentInstanceId);
      setSelectedAnalysisSkillValueState(stored ?? bundledOption.value);
      setSkillsLoading(false);
      return;
    }

    let cancelled = false;
    setSkillsLoading(true);

    void Promise.all([fetchSkills(accessToken), fetchSkillTemplates(accessToken)])
      .then(([skillsRes, templates]) => {
        if (cancelled) {
          return;
        }
        const next: AnalysisSkillOption[] = [];
        for (const t of templates) {
          if (skillTagsIncludeTranscriptAnalysis(t.tags)) {
            next.push({ value: `template:${t.template_key}`, label: `${t.title} · template` });
          }
        }
        for (const s of skillsRes.skills) {
          if (skillTagsIncludeTranscriptAnalysis(s.tags)) {
            next.push({ value: `version:${s.id}`, label: `${s.title} · saved skill` });
          }
        }
        if (!next.some((o) => o.value === bundledOption.value)) {
          next.unshift(bundledOption);
        }
        setAnalysisSkillOptions(next);
        const stored = readAnalysisSkillSelection(assessmentInstanceId);
        const isValid = stored !== null && next.some((o) => o.value === stored);
        setSelectedAnalysisSkillValueState(isValid ? stored : bundledOption.value);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        void message.warning(
          'Could not load skills; using the bundled transcript analysis template.',
        );
        setAnalysisSkillOptions([bundledOption]);
        setSelectedAnalysisSkillValueState(bundledOption.value);
      })
      .finally(() => {
        if (!cancelled) {
          setSkillsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, assessmentInstanceId, bundledOption]);

  const setSelectedAnalysisSkillValue = useCallback(
    (v: string | null) => {
      setSelectedAnalysisSkillValueState(v);
      if (v) {
        writeAnalysisSkillSelection(assessmentInstanceId, v);
      }
    },
    [assessmentInstanceId],
  );

  return {
    analysisSkillOptions,
    selectedAnalysisSkillValue,
    skillsLoading,
    bundledOption,
    setSelectedAnalysisSkillValue,
  };
}
